import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Plus, FileSpreadsheet, Search, Copy, LayoutTemplate, Upload } from "lucide-react";
import { BudgetCsvImport } from "../components/BudgetCsvImport";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { isTech } from "../lib/roles";

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";

export default function Budgets() {
    const { user } = useAuth();
    const readOnly = isTech(user);
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [importOpen, setImportOpen] = useState(false);
    const navigate = useNavigate();

    const load = () => api.get("/budgets").then((r) => { setItems(r.data); setLoading(false); });
    useEffect(() => { load(); }, []);

    const templates = items.filter((b) => b.is_template);
    const regular = items.filter((b) => !b.is_template);

    const applyTemplate = async (tid) => {
        try {
            const { data } = await api.post(`/budgets/${tid}/duplicate?from_template=true`);
            toast.success("Rozpočet vytvořen ze šablony");
            navigate(`/rozpocty/${data.id}`);
        } catch { toast.error("Vytvoření selhalo"); }
    };

    const filtered = regular.filter((b) =>
        !q || b.title?.toLowerCase().includes(q.toLowerCase()) || b.client_name?.toLowerCase().includes(q.toLowerCase()) || b.number?.toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="budgets-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Cenové nabídky</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Rozpočty</h1>
                    <p className="text-sm text-slate-600 mt-2">Sestavte profesionální rozpočet z položek ceníku a exportujte do PDF.</p>
                </div>
                {!readOnly && <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setImportOpen(true)} data-testid="budget-import-button"
                            className="inline-flex items-center gap-2 border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                        <Upload className="w-4 h-4" /> Import soupisu prací (CSV)
                    </button>
                    <Link to="/rozpocty/novy" data-testid="new-budget-button"
                          className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                        <Plus className="w-4 h-4" /> Nový rozpočet
                    </Link>
                </div>}
            </div>
            {importOpen && <BudgetCsvImport onClose={() => { setImportOpen(false); load(); }} />}

            {!readOnly && templates.length > 0 && (
                <div className="bg-slate-900 text-white border-l-4 border-orange-500 mb-6" data-testid="budget-templates">
                    <div className="px-6 py-3 border-b border-slate-800 flex items-center gap-2">
                        <LayoutTemplate className="w-4 h-4 text-orange-400" />
                        <span className="font-display font-bold uppercase tracking-wider text-sm">Šablony rozpočtů</span>
                    </div>
                    <div className="p-4 flex flex-wrap gap-3">
                        {templates.map((t) => (
                            <div key={t.id} className="flex items-center gap-3 bg-slate-800 px-4 py-2.5 border border-slate-700">
                                <Link to={`/rozpocty/${t.id}`} className="text-sm font-medium hover:text-orange-300">{t.title.replace("Šablona: ", "")}</Link>
                                <span className="text-xs text-slate-400 tabular-data">{fmt(t.total)}</span>
                                <button onClick={() => applyTemplate(t.id)} data-testid={`use-template-${t.id}`}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
                                    <Copy className="w-3 h-3" /> Použít
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="bg-white border border-slate-200">
                <div className="flex items-center px-4 py-3 border-b border-slate-200">
                    <Search className="w-4 h-4 text-slate-400 mr-2" />
                    <input placeholder="Hledat rozpočet…" value={q} onChange={(e) => setQ(e.target.value)}
                           className="flex-1 text-sm focus:outline-none bg-transparent" />
                    <span className="text-xs text-slate-400">{filtered.length} / {items.length}</span>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-slate-400 text-xs uppercase tracking-widest">Načítání…</div>
                ) : filtered.length === 0 ? (                    <div className="p-16 text-center">
                        <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">Žádné rozpočty</div>
                        <p className="text-sm text-slate-500 mt-1 mb-6">{readOnly ? "Zatím nebyl vytvořen žádný rozpočet." : "Vytvořte první rozpočet pro vašeho klienta."}</p>
                        {!readOnly && <Link to="/rozpocty/novy" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                            <Plus className="w-4 h-4" /> Nový rozpočet
                        </Link>}
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-900">
                                <th className="px-6 py-3 font-semibold">Číslo</th>
                                <th className="px-6 py-3 font-semibold">Název</th>
                                <th className="px-6 py-3 font-semibold">Klient</th>
                                <th className="px-6 py-3 font-semibold">Platnost</th>
                                <th className="px-6 py-3 font-semibold text-right">Celkem s DPH</th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {filtered.map((b, i) => (
                                <tr key={b.id} className={`border-b border-slate-100 hover:bg-orange-50/40 ${i % 2 ? "bg-slate-50/40" : ""}`}>
                                    <td className="px-6 py-3"><Link to={`/rozpocty/${b.id}`} className="font-mono text-xs text-orange-700 hover:underline">{b.number}</Link></td>
                                    <td className="px-6 py-3"><Link to={`/rozpocty/${b.id}`} className="font-medium text-slate-900 hover:text-orange-600">{b.title}</Link></td>
                                    <td className="px-6 py-3 text-slate-600">{b.client_name || "—"}</td>
                                    <td className="px-6 py-3 text-slate-600">{b.valid_until || "—"}</td>
                                    <td className="px-6 py-3 text-right font-semibold">{fmt(b.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
